<template>
	<div :style="widgetForm.config.style">
		<el-form
			ref="generateForm"
			:inline="inline"
			:model="model"
			:rules="rules"
			:label-position="widgetForm.config.labelPosition"
			:label-width="'auto'"
			:size="widgetForm.config.size"
			:validate-on-rule-change="false"
		>
			<template v-for="(element, index) of widgetForm.list">
				<template v-if="element.type === 'grid'">
					<el-row
						v-if="element.name"
						:key="element.name"
						type="flex"
						:gutter="element.options.gutter ?? 0"
						:justify="element.options.justify"
						:align="element.options.align"
					>
						<el-col v-for="(col, colIndex) of element.columns" :key="colIndex" :span="col.span ?? 0">
							<GenerateFormItem
								v-for="colItem of col.list"
								:key="colItem.name"
								:model="model"
								:element="colItem"
								:upload="upload"
								:disabled="disabled"
								@change="handleChange"
							/>
						</el-col>
					</el-row>
				</template>
				<GenerateFormItem
					v-else
					:key="element.name"
					:model="model"
					:element="widgetForm.list[index]"
					:upload="upload"
					:disabled="disabled"
					@change="handleChange"
				/>
			</template>
			<slot name="footer"></slot>
		</el-form>
	</div>
</template>

<script lang="ts">
import { onMounted, defineComponent, reactive, toRefs, watch, defineEmits } from 'vue'
import GenerateFormItem from './GenerateFormItem.vue'
import { ElMessage } from 'element-plus'
import { useAppStore } from '@/store/modules/app'
import { Base64 } from 'js-base64'
import { deepClone } from '@/utils/tool'
export default defineComponent({
	name: 'GenerateForm',
	components: {
		GenerateFormItem
	},
	props: {
		data: {
			type: Object,
			required: true
		},
		value: {
			type: Object,
			default: () => {}
		},
		dataOption: {
			type: Object,
			default: () => {}
		},
		upload: {
			type: String,
			default: ''
		},
		inline: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	emits: {
		change: (e: any, options: any, user?: any) => {
			return true
		} // 正确声明 'change' 事件
	},
	setup(props, context) {
		const state = reactive({
			generateForm: null as any,
			model: {} as any,
			rules: {} as any,
			widgetForm: JSON.parse(JSON.stringify(props.data))
		})

		const generateModel = (list: any[]) => {
			for (let index = 0; index < list.length; index++) {
				const name = list[index].name
				if (!name) {
					continue
				}

				// 不需要提交表单数据
				if (list[index].type === 'divider') {
					continue
				}

				if (list[index].type === 'grid') {
					list[index].columns.forEach((col: any) => generateModel(col.list))
				} else {
					if (props.value && Object.keys(props.value).includes(name)) {
						// 如果是表格类型，需要特殊处理
						if (list[index].type === 'table') {
							// 表格数据应该是数组格式，如果是字符串则解析
							if (typeof props.value[name] === 'string') {
								try {
									state.model[name] = JSON.parse(props.value[name])
								} catch (e) {
									state.model[name] = []
								}
							} else {
								// 深拷贝，切断与 props.value 的引用共享
								state.model[name] = props.value[name] ? deepClone(props.value[name]) : []
							}
						} else if (list[index].type === 'editor') {
							// 富文本字段需要base64解码
							if (props.value[name] && Base64.isValid(props.value[name])) {
								try {
									state.model[name] = Base64.decode(props.value[name])
								} catch (e) {
									state.model[name] = props.value[name]
								}
							} else {
								state.model[name] = props.value[name]
							}
							console.log(state.model[name], '富文本字段')
						} else {
							const rawValue = props.value[name]
							if (rawValue && typeof rawValue === 'object') {
								// 数组/对象类型（如图片、附件、多选等）做深拷贝，切断与 props.value 的引用共享，
								// 避免子组件编辑时反向修改 props.value，触发父级 deep watch 造成整个表单被重置
								state.model[name] = deepClone(rawValue)
							} else if (rawValue && rawValue.toString() && list[index].options?.dictName) {
								// 如果是字典，则转成字符串
								state.model[name] = rawValue + ''
							} else {
								state.model[name] = rawValue
							}
						}
					} else {
						// 表格类型的默认值应该是空数组
						if (list[index].type === 'table') {
							state.model[name] = []
						} else {
							state.model[name] = list[index].options.defaultValue
						}
					}

					state.rules[name] = list[index].options.rules
				}
			}
		}

		const getDictDataList = (dictList: any[], dictType: string) => {
			const type = dictList.find((item: any) => item.dictType === dictType)
			if (type) {
				return type.dataList
			} else {
				return []
			}
		}

		const generateOptions = (list: any[]) => {
			list.forEach(item => {
				if (item.type === 'grid') {
					item.columns.forEach((col: any) => generateOptions(col.list))
				} else {
					if (item.options.optionsType === 'dict') {
						const dictList = useAppStore().dictList
						const dataList = getDictDataList(dictList, item.options.dictName)
						item.options.options = dataList.map((data: any) => ({
							label: data[item.options.props.label],
							value: data[item.options.props.value]
						}))
					} else if (item.options.optionsType === 'var') {
						if (!props.dataOption) {
							return
						}
						const dataList = props.dataOption[item.options.varName]
						if (!dataList) {
							return
						}
						item.options.options = dataList
					}
				}
			})
		}

		const getData = async () => {
			return await new Promise((resolve, reject) => {
				state.generateForm
					.validate()
					.then((validate: boolean) => {
						if (validate) {
							const processFileUrls = (fileList: any[]) => {
								if (Array.isArray(fileList)) {
									fileList?.forEach((file: any) => {
										if (file.url && file.url.includes('/upload')) {
											const match = file.url.match(/\/upload.*/)
											if (match) {
												file.url = match[0]
											}
										}
									})
								}
							}
							processFileUrls(state.model.imageList)
							processFileUrls(state.model.image25D)
							processFileUrls(state.model.attachmentList)
							processFileUrls(state.model.docList)

							// 处理表格数据
							const processTableData = (list: any[]) => {
								list.forEach(item => {
									if (item.type === 'grid') {
										item.columns.forEach((col: any) => processTableData(col.list))
									} else if (item.type === 'table') {
										const fieldName = item.options.name
										if (state.model[fieldName] && Array.isArray(state.model[fieldName])) {
											// 过滤掉空行
											state.model[fieldName] = state.model[fieldName].filter((row: any) => {
												return Object.values(row).some(value => value !== '' && value !== null && value !== undefined)
											})
										}
									}
								})
							}
							processTableData(state.widgetForm.list)

							const modelToSubmit = JSON.parse(JSON.stringify(state.model))

							const processRichTextFields = (data: any, list: any[]) => {
								list.forEach(item => {
									if (item.type === 'grid') {
										item.columns.forEach((col: any) => processRichTextFields(data, col.list))
									} else if (item.type === 'editor') {
										const fieldName = item.name
										if (data[fieldName] && typeof data[fieldName] === 'string') {
											// 对富文本内容进行base64编码
											try {
												data[fieldName] = Base64.encode(data[fieldName])
											} catch (e) {
												console.error('富文本字段base64编码失败:', fieldName, e)
											}
										}
									}
								})
							}
							processRichTextFields(modelToSubmit, state.widgetForm.list)
							resolve(modelToSubmit)
						} else {
							ElMessage.error('表单效验失败')
						}
					})
					.catch((error: Error) => {
						reject(error)
					})
			})
		}

		const reset = () => {
			state.generateForm.resetFields()
		}
		const handleChange = (e: any, options: any, user?: any) => {
			context.emit('change', e, options, user)
		}
		watch(
			() => props.data,
			val => {
				state.widgetForm = JSON.parse(JSON.stringify(val))
				state.model = {}
				state.rules = {}
				generateModel(state.widgetForm.list)
				generateOptions(state.widgetForm.list)
			},
			{ deep: true, immediate: true }
		)

		watch(
			() => props.value,
			val => {
				state.model = {}
				state.rules = {}
				generateModel(state.widgetForm.list)
			},
			{ deep: true, immediate: true }
		)

		onMounted(() => {
			generateModel(state.widgetForm.list)
			generateOptions(state.widgetForm.list)
		})

		return {
			...toRefs(state),
			getData,
			reset,
			handleChange
		}
	}
})
</script>
